import React, { useMemo } from "react";
import { CheckIcon, Edit3, Settings, MoreHorizontal, Download, Trash2 } from "lucide-react";
import type { BaseEntity, BulkActionGroup, BulkActionWithOnClick } from "../types";

interface UseBulkActionGroupsParams<T extends BaseEntity> {
  bulkEditableFields: Array<{ fieldId: string; label: string }>;
  totalFilteredCount?: number;
  allItemsLength: number;
  allCustomActions: BulkActionWithOnClick<T>[];
  onSetBulkEditField: (field: string) => void;
  onSetShowBulkEditModal: (show: boolean) => void;
  onSelectAllPages: () => void;
}

export function useBulkActionGroups<T extends BaseEntity>({
  bulkEditableFields,
  totalFilteredCount,
  allItemsLength,
  allCustomActions,
  onSetBulkEditField,
  onSetShowBulkEditModal,
  onSelectAllPages,
}: UseBulkActionGroupsParams<T>): BulkActionGroup<T>[] {
  return useMemo(
    () => [
      ...(bulkEditableFields.length > 0
        ? [
            {
              id: "quick",
              label: "Quick Actions",
              icon: <CheckIcon className="h-4 w-4" />,
              actions: bulkEditableFields.slice(0, 3).map((field) => ({
                id: `edit_${field.fieldId}`,
                label: `Edit ${field.label}`,
                icon: <Edit3 className="h-4 w-4" />,
                variant: "outline" as const,
                onClick: async () => {
                  onSetBulkEditField(field.fieldId);
                  onSetShowBulkEditModal(true);
                },
              })),
            } as BulkActionGroup<T>,
          ]
        : []),
      ...(bulkEditableFields.length > 0
        ? [
            {
              id: "fields",
              label: "Edit Fields",
              icon: <Edit3 className="h-4 w-4" />,
              actions: bulkEditableFields.map((field) => ({
                id: `edit_${field.fieldId}`,
                label: `Edit ${field.label}`,
                icon: <Edit3 className="h-4 w-4" />,
                variant: "outline" as const,
                onClick: async () => {
                  onSetBulkEditField(field.fieldId);
                  onSetShowBulkEditModal(true);
                },
              })),
            } as BulkActionGroup<T>,
          ]
        : []),
      {
        id: "system",
        label: "System Actions",
        icon: <Settings className="h-4 w-4" />,
        actions: [
          ...(totalFilteredCount && totalFilteredCount > allItemsLength
            ? [
                {
                  id: "select_all_pages",
                  label: `Select All Pages (${totalFilteredCount.toLocaleString()})`,
                  icon: <CheckIcon className="h-4 w-4" />,
                  variant: "outline" as const,
                  onClick: async () => { onSelectAllPages(); },
                } as BulkActionWithOnClick<T>,
              ]
            : []),
          {
            id: "export",
            label: "Export Selected",
            icon: <Download className="h-4 w-4" />,
            onClick: async (_items: any[], selectedIds: string[]) => {
              console.log("Exporting", selectedIds);
            },
          },
          {
            id: "delete",
            label: "Delete Selected",
            icon: <Trash2 className="h-4 w-4" />,
            variant: "destructive" as const,
            onClick: async (items: any[]) => {
              if (window.confirm(`Delete ${items.length} items?`)) {
                console.log("Deleting");
              }
            },
          },
        ],
      },
      ...(allCustomActions.length > 0
        ? [
            {
              id: "custom",
              label: "Custom Actions",
              icon: <MoreHorizontal className="h-4 w-4" />,
              actions: allCustomActions,
            },
          ]
        : []),
    ],
    [bulkEditableFields, totalFilteredCount, allItemsLength, allCustomActions, onSetBulkEditField, onSetShowBulkEditModal, onSelectAllPages]
  );
}
